import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { locales, type Locale, type Messages } from './messages';

const STORAGE_KEY = 'funnel.admin.locale';

interface I18n {
  locale: Locale;
  t: Messages;
  setLocale: (l: Locale) => void;
}

const I18nContext = createContext<I18n | null>(null);

function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && v in locales;
}

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* storage unavailable */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2).toLowerCase() : 'en';
  return isLocale(nav) ? nav : 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);
  const value = useMemo<I18n>(() => ({ locale, t: locales[locale], setLocale }), [locale, setLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

const LOCALES: Locale[] = ['en', 'ru'];

/** EN / RU pill switch for the admin & dashboard chrome. */
export function LanguageSwitch() {
  const { t, locale, setLocale } = useI18n();
  return (
    <div className="lang-switch" role="group" aria-label={t.language}>
      {LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          className={l === locale ? 'lang active' : 'lang'}
          aria-pressed={l === locale}
          onClick={() => setLocale(l)}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export type { Locale, Messages };
